import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { ExpenseCategory } from "@torre/expenses";
import { useTheme } from "../../../context/ThemeContext";
import { Kpi } from "./ui";
import { CATEGORY_COLORS, categoryLabel, money, monthLabel } from "../lib/format";
import { WARN_AT, isCalendarYear, type BudgetTimeline, type CategoryStatus } from "../lib/budgetTimeline";
import { ui } from "../theme";

// Presupuesto en el tiempo (#1066): portada con el avance del año, plan contra
// real mes a mes con la proyección del resto del año, acumulado y reparto del
// plan por categoría. Misma paleta que las gráficas de Pago de plataformas.

export const PLAN = "#6366f1";
export const REAL = "#0d9488";
export const PROJ = "#d97706";
const OVER = "#dc2626";

const kFormat = (v: unknown) => {
  const n = Number(v);
  return Math.abs(n) >= 1000 ? `$${Math.round(n / 1000)}k` : `$${Math.round(n)}`;
};

export const STATUS_TONES: Record<CategoryStatus, { bar: string; chip: string }> = {
  ok: { bar: "bg-emerald-500", chip: "bg-emerald-500/15 text-emerald-600" },
  warn: { bar: "bg-amber-500", chip: "bg-amber-500/15 text-amber-600" },
  risk: { bar: "bg-orange-500", chip: "bg-orange-500/15 text-orange-600" },
  over: { bar: "bg-red-500", chip: "bg-red-500/15 text-red-600" },
  unbudgeted: { bar: "bg-slate-400", chip: "bg-slate-500/15 text-slate-500" },
  idle: { bar: "bg-slate-300", chip: "bg-slate-500/10 text-slate-400" },
};

/** Barra de avance: relleno = gastado, marca = cierre proyectado (si cabe en la escala). */
export function ProgressBar({ pct, projectedPct, status, label }: { pct: number | null; projectedPct?: number | null; status: CategoryStatus; label: string }) {
  const { isDarkMode } = useTheme();
  const scale = Math.max(100, pct ?? 0, projectedPct ?? 0);
  const width = pct == null ? 100 : Math.min(100, ((pct ?? 0) / scale) * 100);
  return (
    <div className={`relative h-2.5 w-full overflow-hidden rounded-full ${isDarkMode ? "bg-white/10" : "bg-gray-100"}`} role="progressbar" aria-label={label} aria-valuenow={pct == null ? undefined : Math.round(pct)} aria-valuemin={0} aria-valuemax={100}>
      <div className={`h-full rounded-full transition-all ${STATUS_TONES[status].bar}`} style={{ width: `${width}%`, opacity: pct == null ? 0.35 : 1 }} />
      {scale > 100 && <div className="absolute top-0 h-full w-0.5 bg-gray-900/60 dark:bg-white/70" style={{ left: `${(100 / scale) * 100}%` }} title="100%" />}
      {projectedPct != null && projectedPct > 0 && (
        <div className="absolute -top-0.5 h-3.5 w-1 rounded-full" style={{ left: `calc(${Math.min(100, (projectedPct / scale) * 100)}% - 2px)`, background: PROJ }} />
      )}
    </div>
  );
}

function Toggle<T extends string>({ value, options, onChange }: { value: T; options: { value: T; label: string }[]; onChange: (v: T) => void }) {
  const { isDarkMode } = useTheme();
  const c = ui(isDarkMode);
  return (
    <div className={`inline-flex rounded-lg border p-0.5 text-xs ${c.border}`} role="group">
      {options.map((o) => (
        <button key={o.value} type="button" aria-pressed={value === o.value} onClick={() => onChange(o.value)} className={`rounded-md px-2 py-1 ${value === o.value ? "bg-teal-600 text-white" : c.muted}`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export default function BudgetCharts({ timeline, onPickMonth }: { timeline: BudgetTimeline; onPickMonth?: (period: string) => void }) {
  const { t, i18n } = useTranslation(["common", "expensesStatements"]);
  const language = i18n.language;
  const { isDarkMode } = useTheme();
  const c = ui(isDarkMode);
  const [split, setSplit] = useState<"total" | "categoria">("total");
  const tb = (k: string, o?: Record<string, unknown>) => t(`expenses.budgets.charts.${k}`, o);

  const axis = { fontSize: 11, fill: isDarkMode ? "#9ca3af" : "#6b7280" };
  const grid = isDarkMode ? "#333" : "#e5e7eb";
  const tooltipStyle = { fontSize: 12, backgroundColor: isDarkMode ? "#1f1f1f" : "#fff", borderColor: grid, color: isDarkMode ? "#fff" : "#111" };
  const fmt = (v: unknown) => (v == null ? "—" : money(Number(v), "MXN", language));
  const tl = timeline;
  // Si el periodo cruza de año, el mes lleva su año («ene 27») para no repetir etiquetas.
  const spansYears = tl.from.slice(0, 4) !== tl.to.slice(0, 4);
  const short = (p: string) => (spansYears ? `${monthLabel(p, language).split(" ")[0]} ${p.slice(2, 4)}` : monthLabel(p, language).split(" ")[0]);
  // Cómo se nombra el periodo en los títulos: el año, el mes o «desde – hasta» (#1332).
  const scope = isCalendarYear(tl.from, tl.to) ? tl.year : tl.from === tl.to ? monthLabel(tl.from, language) : `${monthLabel(tl.from, language)} – ${monthLabel(tl.to, language)}`;

  const data = useMemo(
    () =>
      tl.months.map((m) => ({
        ...m,
        label: short(m.period),
        real: m.spent,
        plan: m.budget > 0 ? m.budget : null,
        proj: m.phase === "past" ? null : m.projected,
        ...Object.fromEntries(Object.entries(m.budgetByCategory).map(([k, v]) => [`b_${k}`, v])),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tl, language],
  );
  const planCategories = useMemo(() => {
    const set = new Set<ExpenseCategory>();
    tl.months.forEach((m) => Object.keys(m.budgetByCategory).forEach((k) => set.add(k as ExpenseCategory)));
    return [...set];
  }, [tl]);

  const current = tl.months.find((m) => m.phase === "current");
  const todayLabel = current ? short(current.period) : null;
  const hasBudget = tl.totalBudget > 0;
  const pctSpent = hasBudget ? (tl.spentToDate / tl.totalBudget) * 100 : null;
  const pctProjected = hasBudget ? (tl.projectedYearEnd / tl.totalBudget) * 100 : null;
  const elapsedPct = tl.elapsedPct;
  const range = tl.planFrom && tl.planTo ? (tl.planFrom === tl.planTo ? monthLabel(tl.planFrom, language) : `${monthLabel(tl.planFrom, language)} – ${monthLabel(tl.planTo, language)}`) : scope;
  const remaining = tl.totalBudget - tl.spentToDate;
  const yearStatus: CategoryStatus = !hasBudget ? "unbudgeted" : (pctProjected ?? 0) > 100 ? "over" : (pctProjected ?? 0) >= WARN_AT ? "warn" : "ok";

  // Un periodo ya cerrado habla en pasado; uno que va a pasarse sin un mes claro
  // de rebase lo dice igual (#1332): nunca «vas dentro del plan» con margen negativo.
  const closed = tl.currentIndex === 0;
  const gap = tl.totalBudget - (closed ? tl.spentToDate : tl.projectedYearEnd);
  const th = (k: string) => t(`expensesStatements:budgetExec.hero.${k}`, { amount: money(Math.abs(gap), "MXN", language) });
  const headline = !hasBudget
    ? tb("hero.noBudget", { year: scope })
    : closed
      ? th(gap < 0 ? "closedOver" : "closedUnder")
      : gap < 0
        ? tl.overrunMonth
          ? tb("hero.overrun", { month: monthLabel(tl.overrunMonth, language), amount: money(-gap, "MXN", language) })
          : th("over")
        : tb("hero.onTrack", { amount: money(gap, "MXN", language) });

  return (
    <section aria-label={tb("title")} className="space-y-4" data-testid="budget-charts">
      {/* Portada */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-teal-600 via-teal-700 to-indigo-700 p-5 text-white shadow-lg">
        <div className="pointer-events-none absolute -right-10 -top-10 h-44 w-44 rounded-full bg-white/10" />
        <div className="pointer-events-none absolute -bottom-16 right-24 h-40 w-40 rounded-full bg-white/5" />
        <div className="relative grid gap-4 lg:grid-cols-[1.4fr_1fr] lg:items-end">
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-white/70">{tb("hero.kicker", { year: scope })} · {range}</p>
            <p className="mt-1 text-3xl font-bold tabular-nums sm:text-4xl">{money(tl.totalBudget, "MXN", language)}</p>
            <p className="mt-1 text-sm text-white/85" data-testid="budget-headline">
              <i className={`mr-1 ${yearStatus === "over" ? "ri-error-warning-line" : yearStatus === "warn" ? "ri-alarm-warning-line" : "ri-checkbox-circle-line"}`} />
              {headline}
            </p>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-xs text-white/80">
              <span>{tb("hero.spent", { amount: money(tl.spentToDate, "MXN", language), pct: pctSpent == null ? "—" : pctSpent.toFixed(0) })}</span>
              <span>{tb("hero.elapsed", { pct: elapsedPct.toFixed(0) })}</span>
            </div>
            <div className="relative h-3 w-full overflow-hidden rounded-full bg-white/20">
              <div className="h-full rounded-full bg-white" style={{ width: `${Math.min(100, pctSpent ?? 0)}%` }} />
              <div className="absolute top-0 h-full w-0.5 bg-amber-300" style={{ left: `${elapsedPct}%` }} title={tb("hero.today")} />
            </div>
            <p className="text-xs text-white/70">{tb("hero.legend")}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi label={tb("kpi.spent")} value={money(tl.spentToDate, "MXN", language)} hint={tb("kpi.spentHint", { range, year: scope, total: money(tl.spentYear, "MXN", language) })} />
        <Kpi label={remaining < 0 ? tb("kpi.over") : tb("kpi.available")} value={money(Math.abs(remaining), "MXN", language)} tone={remaining < 0 ? "bad" : "good"} hint={tb("kpi.availableHint")} />
        <Kpi label={tb("kpi.projected")} value={money(tl.projectedYearEnd, "MXN", language)} tone={hasBudget ? (tl.projectedYearEnd > tl.totalBudget ? "bad" : "good") : undefined} hint={pctProjected == null ? tb("kpi.projectedNoBudget") : tb("kpi.projectedHint", { pct: pctProjected.toFixed(0) })} />
        <Kpi label={tb("kpi.runRate")} value={money(tl.runRate, "MXN", language)} hint={tb("kpi.runRateHint")} />
      </div>

      <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
        {/* Plan contra real por mes */}
        <div className={c.card}>
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <h2 className={`text-sm font-semibold ${c.text}`}>{tb("monthly.title", { year: scope })}</h2>
              <p className={`mb-2 text-xs ${c.muted}`}>{tb(split === "total" ? "monthly.hint" : "monthly.byCategoryHint")}</p>
            </div>
            <Toggle
              value={split}
              onChange={setSplit}
              options={[
                { value: "total", label: tb("monthly.vsReal") },
                { value: "categoria", label: tb("monthly.byCategory") },
              ]}
            />
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              {split === "total" ? (
                <ComposedChart data={data} margin={{ top: 18, right: 8 }} onClick={(e) => {
                    // recharts 3: el clic trae el índice del mes, no su payload.
                    const i = Number(e?.activeIndex);
                    const p = Number.isInteger(i) ? data[i]?.period : undefined;
                    if (p && onPickMonth) onPickMonth(p);
                  }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={grid} vertical={false} />
                  <XAxis dataKey="label" tick={axis} />
                  <YAxis tick={axis} tickFormatter={kFormat} width={52} />
                  <Tooltip formatter={(v, name) => [fmt(v), String(name)]} contentStyle={tooltipStyle} cursor={{ fill: isDarkMode ? "#ffffff10" : "#0000000a" }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar isAnimationActive={false} dataKey="real" name={tb("series.real")} fill={REAL} radius={[4, 4, 0, 0]} maxBarSize={26}>
                    {data.map((d) => (
                      <Cell key={d.period} fill={d.real != null && d.budget > 0 && d.real > d.budget ? OVER : REAL} />
                    ))}
                  </Bar>
                  <Bar isAnimationActive={false} dataKey="proj" name={tb("series.projected")} fill={PROJ} fillOpacity={0.45} stroke={PROJ} strokeDasharray="4 3" radius={[4, 4, 0, 0]} maxBarSize={26} />
                  <Line isAnimationActive={false} type="stepAfter" dataKey="plan" name={tb("series.plan")} stroke={PLAN} strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} />
                  {todayLabel && <ReferenceLine x={todayLabel} stroke={PROJ} strokeDasharray="3 3" label={{ value: tb("hero.today"), fill: PROJ, fontSize: 11, position: "top" }} />}
                </ComposedChart>
              ) : (
                <BarChart data={data} margin={{ top: 18, right: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={grid} vertical={false} />
                  <XAxis dataKey="label" tick={axis} />
                  <YAxis tick={axis} tickFormatter={kFormat} width={52} />
                  <Tooltip formatter={(v, name) => [fmt(v), String(name)]} contentStyle={tooltipStyle} cursor={{ fill: isDarkMode ? "#ffffff10" : "#0000000a" }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {planCategories.map((k, i) => (
                    <Bar isAnimationActive={false} key={k} dataKey={`b_${k}`} stackId="plan" name={categoryLabel(k, language)} fill={CATEGORY_COLORS[k]} radius={i === planCategories.length - 1 ? [4, 4, 0, 0] : undefined} maxBarSize={30} />
                  ))}
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>
          {split === "total" && onPickMonth && <p className={`mt-1 text-xs ${c.muted}`}>{tb("monthly.clickHint")}</p>}
        </div>

        {/* Acumulado */}
        <div className={c.card}>
          <h2 className={`text-sm font-semibold ${c.text}`}>{tb("cumulative.title", { year: scope })}</h2>
          <p className={`mb-2 text-xs ${c.muted}`}>{tb("cumulative.hint")}</p>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={data} margin={{ top: 18, right: 8 }}>
                <defs>
                  <linearGradient id="budgetPlanFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={PLAN} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={PLAN} stopOpacity={0.03} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={grid} vertical={false} />
                <XAxis dataKey="label" tick={axis} />
                <YAxis tick={axis} tickFormatter={kFormat} width={56} />
                <Tooltip formatter={(v, name) => [fmt(v), String(name)]} contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Area isAnimationActive={false} type="monotone" dataKey="cumBudget" name={tb("series.planCum")} stroke={PLAN} strokeWidth={2} fill="url(#budgetPlanFill)" />
                <Line isAnimationActive={false} type="monotone" dataKey="cumExpected" name={tb("series.projectedCum")} stroke={PROJ} strokeWidth={2} strokeDasharray="6 4" dot={false} />
                <Line isAnimationActive={false} type="monotone" dataKey="cumSpent" name={tb("series.realCum")} stroke={REAL} strokeWidth={3} dot={{ r: 3, strokeWidth: 2, fill: isDarkMode ? "#1a1a19" : "#fff" }} connectNulls={false} />
                {todayLabel && <ReferenceLine x={todayLabel} stroke={PROJ} strokeDasharray="3 3" />}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <p className={`mt-1 text-xs ${c.muted}`}>{tb("cumulative.footnote")}</p>
        </div>
      </div>

      {/* Tarjetas por categoría */}
      <div className={c.card}>
        <h2 className={`text-sm font-semibold ${c.text}`}>{tb("categories.title", { year: scope })}</h2>
        <p className={`mb-3 text-xs ${c.muted}`}>{tb("categories.hint")}</p>
        {tl.categories.length === 0 ? (
          <p className={`py-6 text-center text-sm ${c.muted}`}>{tb("categories.empty")}</p>
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {tl.categories.map((cat) => {
              const projectedPct = cat.budget > 0 ? (cat.projected / cat.budget) * 100 : null;
              const left = cat.budget - cat.spent;
              return (
                <div key={cat.category} className={`rounded-xl border p-3 ${c.border} ${c.surface}`} data-testid={`budget-cat-${cat.category}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className={`inline-flex items-center gap-2 text-sm font-medium ${c.text}`}>
                      <span className="h-2.5 w-2.5 rounded-full" style={{ background: CATEGORY_COLORS[cat.category] }} />
                      {categoryLabel(cat.category, language)}
                    </span>
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_TONES[cat.status].chip}`}>{tb(`status.${cat.status}`)}</span>
                  </div>
                  <div className="mt-2 flex items-baseline justify-between gap-2">
                    <span className={`text-lg font-semibold tabular-nums ${c.text}`}>{money(cat.spent, "MXN", language)}</span>
                    <span className={`text-xs tabular-nums ${c.muted}`}>{cat.budget > 0 ? tb("categories.of", { amount: money(cat.budget, "MXN", language) }) : tb("status.unbudgeted")}</span>
                  </div>
                  <div className="mt-2">
                    <ProgressBar pct={cat.pct} projectedPct={projectedPct} status={cat.status} label={categoryLabel(cat.category, language)} />
                  </div>
                  <p className={`mt-2 text-xs ${c.muted}`}>
                    {cat.budget > 0
                      ? left >= 0
                        ? tb("categories.left", { amount: money(left, "MXN", language), pct: (cat.pct ?? 0).toFixed(0) })
                        : tb("categories.exceeded", { amount: money(-left, "MXN", language) })
                      : tb("categories.noPlan")}
                  </p>
                  <p className="mt-0.5 text-xs" style={{ color: PROJ }}>
                    {tb("categories.projected", { amount: money(cat.projected, "MXN", language), rate: money(cat.runRate, "MXN", language) })}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
